import { useEffect, useRef, type ReactNode } from "react";
import { InteractiveElementShell } from "@/components/interactive/InteractiveElementShell";
import QuizResultSummary from "@/components/storyboard/quiz/QuizResultSummary";
import QuizReviewList from "@/components/storyboard/quiz/review/QuizReviewList";
import { useCourseDetailStore } from "@/stores/courseDetailStore";
import { useReportInteractiveLearningProgress } from "@/hooks/useReportInteractiveLearningProgress";
import {
  getInteractiveElementState,
  setInteractiveElementState,
  InteractiveElementStatusKind,
  type InteractiveElementScore,
  type InteractiveElementStatus,
} from "@/lib/interactiveElementState";
import type { InteractiveIllustrationKind } from "@/lib/interactiveIllustration";
import { isQuizReview, type QuizReview } from "@/lib/quizReview";

export interface QuizRenderArgs<TPartial> {
  initialPartial?: TPartial;
  initialIndex?: number;
  onPartialChange: (partial: TPartial, index: number) => void;
  onComplete: (score?: InteractiveElementScore, review?: QuizReview) => void;
  onFinish: () => void;
}

export interface QuizInteractiveFlowProps<TPartial> {
  id: string;
  title: string;
  illustrationKind: InteractiveIllustrationKind;
  supportsInProgress?: boolean;
  statusOverride?: InteractiveElementStatus;
  /** Overlay size; "wide" gives code editors room to work. */
  size?: "default" | "wide";
  /**
   * When "inline", the quiz renders in the blue card without a start CTA or
   * modal. Defaults to "modal".
   */
  layout?: "modal" | "inline";
  /** Wording of the CTA that leaves the completed overlay (default "Fertig"). */
  completedCtaLabel?: string;
  /** Shows that CTA while the card stays a start card. See the shell prop. */
  showCloseCta?: boolean;
  /**
   * When false, a finished card skips the Abgeschlossen pill. Angefangen still
   * shows while the quiz can be worked on.
   */
  showCompletedBadge?: boolean;
  /**
   * When set, the quiz reports its progress to the server `LearningProgress`
   * record as the local interactive status transitions — in-progress on the
   * first interaction, completed once finished. Used by the code quizzes, whose
   * progress is validation-driven rather than scroll-into-view driven.
   */
  reportProgress?: boolean;
  renderQuiz: (args: QuizRenderArgs<TPartial>) => ReactNode;
}

type CompletedQuizStatus = Extract<
  InteractiveElementStatus,
  { kind: InteractiveElementStatusKind.Completed }
>;

/**
 * Card content after the quiz is done — inline review when available, else score.
 */
const QuizCompletedView = ({
  status,
  isEditing,
}: {
  status: CompletedQuizStatus;
  isEditing: boolean;
}) => (
  <div className="flex w-full flex-col gap-4">
    <QuizCompletedBody status={status} isEditing={isEditing} />
  </div>
);

const QuizCompletedBody = ({
  status,
  isEditing,
}: {
  status: CompletedQuizStatus;
  isEditing: boolean;
}) => {
  const learnerIsLookingAtCompletedCard = !isEditing;
  const review =
    learnerIsLookingAtCompletedCard && isQuizReview(status.review)
      ? status.review
      : null;
  const reviewHasQuestions =
    review != null && (review.items?.filter(Boolean).length ?? 0) > 0;

  if (reviewHasQuestions) {
    return <QuizReviewList review={review} />;
  }

  return <QuizResultSummary score={status.score} compact />;
};

const resumeArgsFromStatus = <TPartial,>(
  status: InteractiveElementStatus
): Pick<QuizRenderArgs<TPartial>, "initialPartial" | "initialIndex"> => {
  if (status.kind !== InteractiveElementStatusKind.InProgress) {
    return {};
  }

  return {
    initialPartial: status.partial as TPartial,
    initialIndex: status.lastTaskIndex ?? 0,
  };
};

/**
 * Shared interactive-element wiring for quiz subtypes — card/modal shell,
 * in-progress persistence, optional server progress reporting, and completed
 * review chrome. Inline quizzes skip Fertig and show the review as soon as
 * scoring finishes; modal quizzes keep Fertig to close the overlay.
 */
const QuizInteractiveFlow = <TPartial,>({
  id,
  title,
  illustrationKind,
  supportsInProgress,
  statusOverride,
  size,
  layout = "modal",
  completedCtaLabel,
  showCloseCta,
  reportProgress,
  renderQuiz,
  showCompletedBadge,
}: QuizInteractiveFlowProps<TPartial>) => {
  const { enrollmentId } = useCourseDetailStore();
  const partialRef = useRef<TPartial | null>(null);
  const indexRef = useRef<number>(0);
  const completedRef = useRef<boolean>(false);
  const isInline = layout === "inline";

  // Keep a stable onPartialChange identity so quiz Effects that depend on it
  // do not re-fire when markInProgress notifies and re-renders this flow.
  const persistPartialRef = useRef<(partial: TPartial, index: number) => void>(
    () => undefined
  );

  const saveProgressSoLearnerCanResume = () => {
    persistPartialRef.current = (partial, index) => {
      partialRef.current = partial;
      indexRef.current = index;

      // Store in-progress answers as soon as the learner interacts so they can
      // continue later (inline has no overlay close; also covers remount resume).
      const learnerAlreadyFinished = completedRef.current;
      const quizDoesNotSupportResume = !supportsInProgress;
      const quizDoesNotTrackResumeProgress = !(reportProgress || isInline);
      if (
        learnerAlreadyFinished ||
        quizDoesNotSupportResume ||
        quizDoesNotTrackResumeProgress
      ) {
        return;
      }

      const learnerAlreadyCompletedThisQuiz =
        getInteractiveElementState(id, enrollmentId).kind ===
        InteractiveElementStatusKind.Completed;
      if (learnerAlreadyCompletedThisQuiz) return;

      setInteractiveElementState(id, enrollmentId, {
        kind: InteractiveElementStatusKind.InProgress,
        partial,
        lastTaskIndex: index,
      });
    };
  };
  useEffect(saveProgressSoLearnerCanResume);

  const onPartialChangeStable = useRef((partial: TPartial, index: number) => {
    persistPartialRef.current(partial, index);
  }).current;

  // A status the server already has is not new progress when it is copied
  // onto a not-started element. Finishing the quiz in this session still is.
  const serverStatusAlreadyOnRecord =
    statusOverride &&
    statusOverride.kind !== InteractiveElementStatusKind.NotStarted
      ? statusOverride.kind
      : undefined;

  useReportInteractiveLearningProgress(id, {
    enabled: Boolean(reportProgress),
    includeInProgress: true,
    alreadyReportedKind: serverStatusAlreadyOnRecord,
  });

  // The status icon of the element (TrackedContentElement) reads this store, so
  // a status the server derived has to be shown there too — otherwise it stays
  // "not started" after a reload while the card already shows the result. The
  // code quizzes leave this out: useCodeQuizProgress writes their status itself.
  const showServerStatusOnBadge = () => {
    if (!reportProgress || !statusOverride || !enrollmentId) return;
    if (statusOverride.kind === InteractiveElementStatusKind.NotStarted) return;
    const current = getInteractiveElementState(id, enrollmentId);
    if (current.kind === InteractiveElementStatusKind.Completed) return;
    if (current.kind === statusOverride.kind) return;

    setInteractiveElementState(id, enrollmentId, statusOverride);
  };
  useEffect(showServerStatusOnBadge, [
    id,
    enrollmentId,
    reportProgress,
    statusOverride,
  ]);

  const handleBeforeClose = () => {
    if (completedRef.current) {
      completedRef.current = false;
      partialRef.current = null;
      indexRef.current = 0;
      return;
    }
    const current = getInteractiveElementState(id, enrollmentId);
    if (current.kind === InteractiveElementStatusKind.Completed) return;

    if (!supportsInProgress) {
      setInteractiveElementState(id, enrollmentId, {
        kind: InteractiveElementStatusKind.NotStarted,
      });
    } else if (partialRef.current != null) {
      setInteractiveElementState(id, enrollmentId, {
        kind: InteractiveElementStatusKind.InProgress,
        partial: partialRef.current,
        lastTaskIndex: indexRef.current,
      });
    }
    partialRef.current = null;
    indexRef.current = 0;
  };

  return (
    <InteractiveElementShell
      contentElementId={id}
      enrollmentId={enrollmentId}
      title={title}
      illustrationKind={illustrationKind}
      size={size}
      layout={layout}
      completedCtaLabel={completedCtaLabel}
      showCloseCta={showCloseCta}
      showCompletedBadge={showCompletedBadge}
      notStartedCta={isInline ? undefined : { label: "Quiz starten" }}
      inProgressCta={isInline ? undefined : { label: "Quiz fortsetzen" }}
      supportsInProgress={supportsInProgress}
      statusOverride={statusOverride}
      onBeforeClose={handleBeforeClose}
      completedView={(status, _api, { isEditing }) => (
        <QuizCompletedView status={status} isEditing={isEditing} />
      )}
      editingView={({ status, complete, close }) =>
        renderQuiz({
          ...resumeArgsFromStatus<TPartial>(status),
          onPartialChange: onPartialChangeStable,
          onComplete: (score, review) => {
            completedRef.current = true;
            complete(score, review);
            // Inline quizzes already recap answers in the completed card, so
            // skip the shell's Fertig step. Modal quizzes keep it to close
            // the overlay (code editors).
            if (isInline) close();
          },
          onFinish: close,
        })
      }
    />
  );
};

export default QuizInteractiveFlow;
